import { useMemo } from "react";
import { UI, FONT } from "../constants/tokens";
import { fmtPct, shownDir, MINUS } from "../utils/formatters";
import { moversWithRest } from "../utils/portfolioMovers";
import { fromLastSession, lastSessionDay } from "../utils/marketStatus";
import { extendedFor } from "../utils/extendedHours";
import EmptyState from "./EmptyState";
import InfoTip from "./InfoTip";
import LTR from "./LTR";
import { Skeleton as Skel, Loading } from "./Skeleton";

// ─── "מה הזיז את התיק" (sprint 18, stage 1 · redrawn in sprint 19, stage 3) ─
// Each holding's share of the day's move, in percentage points of the
// portfolio (utils/portfolioMovers.js: daily change × weight at the previous
// close, so the parts add up to the whole). Arithmetic only: no model writes
// a word here, and no string joins a number to a cause.
//
// Sprint 19: a bar per holding around a zero line in the middle · a fall
// reaches left in red, a rise right in green, the length its share of the
// largest bar on the card. The bar is drawn LTR whatever the page direction,
// as a number line is. The three largest, then the rest as one row. The
// articles that sat under each row are gone: a row opens the company page,
// where they are.

const TOP = 3;

const tone = v => (shownDir(v) > 0 ? UI.gain : shownDir(v) < 0 ? UI.loss : UI.text.primary);

// "−4.70" · the real minus, no sign on a figure that rounds to zero.
export function fmtPoints(v, decimals = 2) {
  const s = Math.abs(v).toFixed(decimals);
  if (!/[1-9]/.test(s)) return (0).toFixed(decimals);
  return `${v < 0 ? MINUS : "+"}${s}`;
}

// The S&P 500 line of the summary, only when its quote is from the same
// session the portfolio figure is from. A Yahoo quote carries the moment the
// exchange stamped it; a FRED close carries its date only.
export function indexForSession(sp, market) {
  if (!sp || !Number.isFinite(sp.change_pct)) return null;
  const same = sp.quote_ts ? fromLastSession(sp.quote_ts, market) : sp.date === market.lastSession;
  if (!same) return null;
  return { pct: sp.change_pct, source: sp.quote_ts ? "Yahoo Finance" : "FRED" };
}

// The bar: a zero line in the middle, the bar to its left or right.
function Bar({ points, scale }) {
  const d = shownDir(points);
  const share = scale > 0 ? Math.min(1, Math.abs(points) / scale) : 0;
  return (
    <span dir="ltr" aria-hidden="true" style={{ display: "block", position: "relative", height: 8, marginTop: 8 }}>
      <span style={{ position: "absolute", left: "50%", top: -3, bottom: -3, width: 1, background: UI.line.strong }}/>
      {d !== 0 && (
        <span style={{
          position: "absolute", top: 0, bottom: 0, borderRadius: 4,
          background: d > 0 ? UI.gain : UI.loss,
          width: `max(2px, ${share * 50}%)`,
          ...(d > 0 ? { left: "50%" } : { right: "50%" }),
        }}/>
      )}
    </span>
  );
}

// Spans only, displayed as blocks: a row is a <button>, which takes phrasing
// content (stage 3 review).
function Row({ title, points, sub, tag, scale, onClick, label }) {
  const inner = (
    <>
      <span style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 10 }}>
        <span style={{ display: "inline-flex", alignItems: "baseline", gap: 8, minWidth: 0, flexWrap: "wrap" }}>
          {title}
          {tag}
        </span>
        <span style={{ fontSize: 15, fontWeight: 800, color: UI.text.primary, whiteSpace: "nowrap" }}>
          <LTR className="si-num" style={{ color: tone(points) }}>{fmtPoints(points)}</LTR> נק׳
        </span>
      </span>
      {sub && <span style={{ display: "block", fontSize: 12.5, color: UI.text.muted, marginTop: 2 }}>{sub}</span>}
      <Bar points={points} scale={scale}/>
    </>
  );
  const style = { display: "block", width: "100%", textAlign: "start", padding: "12px 0", background: "transparent", border: "none", fontFamily: "inherit", fontSize: "inherit", lineHeight: "inherit", color: "inherit" };
  return onClick
    ? <button type="button" onClick={onClick} title={label} style={{ ...style, cursor: "pointer" }}>{inner}</button>
    : <div style={style}>{inner}</div>;
}

const STATE_WORD = { PRE: "טרום מסחר", POST: "אחרי הסגירה" };

// "טרום מסחר +0.40%" · only when the holding has a fresh extended trade.
function ExtendedTag({ move }) {
  if (!move) return null;
  return (
    <span style={{ fontSize: 12, fontWeight: 700, color: UI.text.secondary, background: UI.fill.subtle, borderRadius: 999, padding: "2px 8px", whiteSpace: "nowrap" }}>
      {STATE_WORD[move.state]} <LTR className="si-num" style={{ color: tone(move.pct) }}>{fmtPct(move.pct)}</LTR>
    </span>
  );
}

export default function PortfolioMoversCard({ portfolio, market, extended = null, loading = false, onOpenStock, cardStyle }) {
  const { rows, rest, total, scale } = useMemo(() => moversWithRest(portfolio, TOP), [portfolio]);
  const when = market.open ? "היום" : `יום המסחר האחרון · ${lastSessionDay(market)}`;
  const now = new Date();
  const moves = Object.fromEntries(rows.map(r => [r.symbol, extendedFor(extended?.quotes?.[String(r.symbol).toUpperCase()], now)]));
  const anyTag = Object.values(moves).some(Boolean);

  if (loading) {
    return (
      <div style={cardStyle}>
        <Loading><Skel w="55%" h={16} mb={12}/><Skel w="85%" h={14} mb={8}/><Skel w="75%" h={14}/></Loading>
      </div>
    );
  }
  if (!rows.length || !total) {
    return (
      <div style={cardStyle}>
        <EmptyState icon="chart" line="אין עדיין נתוני שינוי יומי להחזקות" sub={<>הנתונים מתעדכנים עם קבלת מחירים מ-<LTR>Finnhub</LTR></>}/>
      </div>
    );
  }

  return (
    <div style={cardStyle} data-movers>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12.5, color: UI.text.muted }}>{when}</span>
        <span style={{ fontSize: 14, color: UI.text.secondary }}>
          התיק <LTR className="si-num" style={{ fontWeight: 800, color: tone(total.pct) }}>{fmtPct(total.pct)}</LTR>
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", marginTop: 4 }}>
        {rows.map((r, i) => (
          <div key={r.symbol} style={{ borderTop: i === 0 ? "none" : `1px solid ${UI.line.hairline}` }}>
            <Row
              title={<LTR style={{ fontFamily: FONT.latin, fontSize: 15, fontWeight: 800, color: UI.text.primary }}>{r.symbol}</LTR>}
              tag={<ExtendedTag move={moves[r.symbol]}/>}
              points={r.points}
              sub={<><LTR className="si-num" style={{ color: tone(r.pct) }}>{fmtPct(r.pct)}</LTR> · משקל <LTR className="si-num">{fmtPct(r.weight, { decimals: 1, signed: false })}</LTR></>}
              scale={scale}
              onClick={onOpenStock ? () => onOpenStock(r.symbol) : null}
              label={`לעמוד החברה · ${r.symbol}`}
            />
          </div>
        ))}
        {rest && (
          <div style={{ borderTop: `1px solid ${UI.line.hairline}` }}>
            <Row
              title={<span style={{ fontSize: 14, fontWeight: 700, color: UI.text.secondary }}>{rest.count === 1 ? "עוד החזקה אחת" : <>עוד <LTR className="si-num">{rest.count}</LTR> החזקות</>}</span>}
              points={rest.points}
              scale={scale}
            />
          </div>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", fontSize: 11.5, color: UI.text.muted, marginTop: 8, lineHeight: 1.6 }}>
        <span>
          נק׳ = נקודות אחוז מהתיק. הסכום של כל ההחזקות שווה לשינוי של התיק.
          {" "}מחירים מ-<LTR>Finnhub</LTR>{anyTag && <> · מסחר מורחב מ-<LTR>Yahoo Finance</LTR></>}
        </span>
        <InfoTip label="איך מחושבת התרומה" size={13}
          text={"תרומה = השינוי היומי של ההחזקה × משקלה בתיק לפי השווי בסגירה הקודמת. כך סכום התרומות של כל ההחזקות שווה לשינוי של התיק. אורך כל פס יחסי לתרומה הגדולה בכרטיס. הנקודות מעוגלות לשתי ספרות אחרי הנקודה."}/>
      </div>
    </div>
  );
}
